import { z } from 'zod';
import {
  CANNON_CATALOG,
  MAX_SHIP_LEVEL_CATALOG,
  SHIP_CATALOG,
  type ShipCatalogEntry,
} from '../catalog';
import {
  EQUIPMENT_DEFINITION_ENTRIES,
  EQUIPMENT_MAX_LEVEL,
  type EquipmentComponent,
  type EquipmentDefinitionEntry,
  QUANTUM_STABILIZER_ID,
  QUANTUM_STABILIZER_RARITY,
} from '../equipment/catalog';
import { CANNON_RARITIES, type CannonRarity, type Rarity } from '../rarity';
import { SKIN_MANIFEST } from '../skins/manifest';
import { ARTIFACT_DEFINITIONS, type ArtifactDefinition } from './artifacts';
import {
  CARD_CONTENT_VERSION,
  CARD_DEFINITIONS,
  CARD_MAX_LEVEL,
  type CardDefinition,
} from './cards';
import {
  JEWEL_CONTENT_VERSION,
  JEWEL_DEFINITIONS,
  JEWEL_MAX_LEVEL,
  type JewelDefinition,
} from './jewels';
import {
  PET_FOODS,
  PET_UNLOCK_CONSUMABLES,
  type PetFoodDefinition,
  type PetUnlockConsumable,
} from './pet-consumables';
import { PET_DEFINITIONS, PET_MAX_EVOLUTION, type PetDefinition, RETIRED_PET_IDS } from './pets';
import { SKIN_DEFINITIONS, type SkinDefinitionEntry } from './skins';

/**
 * Authoritative item-definition catalog for the vault (ADR 0004).
 *
 * A DEFINITION says what an item IS: category, rarity, how it is stored and
 * how far it levels. What a player OWNS lives in the database
 * (`packages/database/src/schema/inventory.ts`), always referencing a
 * definition id from this file. The database carries a seeded projection of
 * this catalog so foreign keys and level triggers can enforce it; an
 * integration test keeps the two in sync.
 *
 * Two things are deliberately NOT here:
 *
 * - **Currencies.** Gold and Cash are ledger balances, never items. There is
 *   no definition for them and the category list cannot express one.
 * - **Invented content.** Skins and consumables are recognised CATEGORIES (the
 *   vault must not need a migration to store them), but no definition exists
 *   until the content is actually designed.
 */

/* -------------------------------------------------------------------------- */
/*  Vocabulary                                                                */
/* -------------------------------------------------------------------------- */

export const ITEM_CATEGORIES = [
  'ship',
  'cannon',
  'artifact',
  'pet',
  'skin',
  'consumable',
  /**
   * The five energy-system components (prompt 7A.1).
   *
   * Its own category rather than a variant of `ship`, because the five slots,
   * the per-instance roll and the upgrade-with-destruction are rules no other
   * category has - and because an equipped component must be refused by trade
   * while an owned artifact must not.
   */
  'equipment',
  /**
   * As joias (gate V28B).
   *
   * Categoria própria e não uma variante de `skin`, porque as regras são
   * outras: uma joia ocupa um socket de uma cópia de skin ou é passiva por
   * posse, e a skin em si continua a não ter nível nem atributo. A distinção
   * entre as duas naturezas vive em `jewels.ts`, no campo `socketMode`.
   */
  'jewel',
  /**
   * As cartas (gate V28H.C).
   *
   * Categoria própria e não uma variante de `consumable`, porque uma carta não
   * se gasta: ela fica, sobe de nível fundindo duas cópias, e o que ela dá é
   * uma habilidade que se dispara e volta a ficar em espera. Um consumível
   * desaparece ao ser usado, que é a única coisa que os dois pareciam ter em
   * comum.
   *
   * Também não é uma variante de `artifact`, embora funda pela mesma regra: um
   * artefacto vale por ser possuído e uma carta só vale enquanto estiver num
   * dos três espaços que um gate seguinte cria.
   */
  'card',
] as const;
export type ItemCategory = (typeof ITEM_CATEGORIES)[number];
export const itemCategorySchema = z.enum(ITEM_CATEGORIES);

/**
 * How ownership is stored: `instance` items have individual identity (a ship
 * with its own uuid and level), `stack` items are a per-definition quantity
 * (potions, materials).
 */
export const ITEM_STORAGE_KINDS = ['instance', 'stack'] as const;
export type ItemStorageKind = (typeof ITEM_STORAGE_KINDS)[number];

/**
 * Lifecycle of an owned instance. `owned` is the only usable state; the other
 * two are terminal - a consumed or retired instance is never deleted and
 * never comes back, so the history stays auditable.
 */
export const ITEM_INSTANCE_STATES = ['owned', 'consumed', 'retired'] as const;
export type ItemInstanceState = (typeof ITEM_INSTANCE_STATES)[number];

/** Why an item is locked. Market and trade do not exist yet; the seam does. */
export const RESERVATION_PURPOSES = [
  'market_listing',
  'direct_trade',
  'merge',
  'system_operation',
] as const;
export type ReservationPurpose = (typeof RESERVATION_PURPOSES)[number];

export const RESERVATION_STATUSES = ['active', 'released', 'consumed', 'expired'] as const;
export type ReservationStatus = (typeof RESERVATION_STATUSES)[number];

/** Append-only inventory history event types. */
export const INVENTORY_EVENT_TYPES = [
  'grant',
  'reserve',
  'release',
  'transfer_out',
  'transfer_in',
  'consume',
  'merge_input',
  'merge_output',
  'retire',
  'stack_credit',
  'stack_debit',
  /**
   * Permanent training applied to ONE pet instance.
   *
   * Its own type rather than `consume`: consuming says an item ceased to
   * exist, and what happened here is that a counter on a DIFFERENT item went
   * up. The food's own disappearance is the `stack_debit` beside it.
   */
  'train',
  /**
   * A palette variant unlocked for the ACCOUNT.
   *
   * Also its own type: nothing was granted and nothing was consumed on the
   * instance side - an account gained a permanent capability.
   */
  'unlock',
  /**
   * One equipment upgrade attempt, whatever it produced.
   *
   * Its own type, and one row per ATTEMPT rather than only per success: an
   * inventory that is one Gold poorer and one level unchanged has to be
   * explicable, and so does one that is an item short. The outcome
   * (`succeeded` / `failed` / `protected` / `destroyed`), the level before and
   * after, the currency and the amount all travel in the event's metadata. A
   * destruction ALSO writes the ordinary `retire`, because the instance really
   * did leave the inventory.
   */
  'upgrade',
] as const;
export type InventoryEventType = (typeof INVENTORY_EVENT_TYPES)[number];

/** Same vocabulary as the audit log's `actor_kind`. */
export const INVENTORY_ACTOR_KINDS = ['player', 'admin', 'system', 'webhook'] as const;
export type InventoryActorKind = (typeof INVENTORY_ACTOR_KINDS)[number];

/* -------------------------------------------------------------------------- */
/*  Level caps                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Maximum level per category. `null` means the progression rule for that
 * category is PENDING (docs/OPEN_QUESTIONS.md): the vault stores such items
 * at level 1 only, and nothing may invent a curve for them.
 *
 * Ship/cannon/artifact mirror `MAX_LEVEL_BY_CATEGORY` in
 * `@kenocyber/game-domain` (merge.ts); a cross-check test keeps them equal.
 */
export const ITEM_LEVEL_CAPS: Readonly<Record<ItemCategory, number | null>> = Object.freeze({
  ship: MAX_SHIP_LEVEL_CATALOG,
  /**
   * **Cinco** desde o gate V28I.CH.11A (ADR 0138); era 7. Espelha
   * `MAX_CANNON_LEVEL` de `game-domain`, que este pacote não pode importar —
   * e é `inventory-caps.test.ts` que impede os dois de divergirem.
   */
  cannon: 5,
  artifact: 10,
  /**
   * A pet's `level` column IS its evolution: MK I, MK II, MK III.
   *
   * Reusing the column rather than adding an `evolution` one is deliberate -
   * the vault already caps levels in a database trigger, merging already
   * consumes two of one level to make one of the next, and the append-only
   * history already records both sides. A parallel column would have
   * duplicated all three and given them a chance to disagree.
   */
  pet: PET_MAX_EVOLUTION,
  /**
   * A skin has NO level, and `null` is how this catalog says that.
   *
   * It said 7 before, mirroring a cannon, and that was never a decision - it
   * was the shape of the nearest neighbour copied into an empty category. A
   * skin is not merged, does not level and has no progression: what it will
   * eventually carry is SOCKETS, which are a different mechanism entirely and
   * are not part of this foundation.
   *
   * `null` is the same thing `consumable` says, and the database enforces it in
   * one place: the level trigger from migration 0003 refuses any level but 1
   * for a definition whose `max_level` is null. Nothing had to be migrated,
   * because no skin definition exists to have a row.
   */
  skin: null,
  consumable: null,
  /**
   * A component levels 1..10 by UPGRADING, never by merging two copies.
   *
   * The same cap number as an artifact and a completely different mechanism:
   * an artifact doubles its value per level and is merged, a component
   * multiplies its immutable roll by a compounding table and is upgraded with
   * a chance of destruction. `EQUIPMENT_MAX_LEVEL` is the source; this mirrors
   * it so the database trigger has a number to enforce.
   */
  equipment: EQUIPMENT_MAX_LEVEL,
  /**
   * Uma joia vai ao nível **30**, e é o teto final (gate V28G.K).
   *
   * Era `null` - "regra pendente, nível 1 apenas" - e a decisão fechou: 1..30,
   * inteiro, sem nada a crescer depois do 30. A migração 0051 põe o mesmo 30 em
   * `item_definitions.max_level`, e daí em diante o gatilho de nível da 0003
   * recusa 0 e recusa 31 sem uma linha de código nova.
   *
   * O que este número NÃO traz é uma forma de lá chegar. Não há upgrade, fusão,
   * custo, moeda, rota nem botão; o que existe é a REPRESENTAÇÃO correta e as
   * invariantes a segurá-la. O sistema de subida é uma decisão separada.
   *
   * O teto vive na CATEGORIA, portanto vale também para a peça soberana - e
   * para ela é inerte. Um Coração Primordial vale pela CONTAGEM incorporada na
   * nave; nenhum efeito seu lê o nível da cópia, e nenhuma rota o sobe. Um teto
   * por definição faria a base e este catálogo poderem discordar, que é um preço
   * pior do que um número que ninguém usa.
   */
  /*
   * LIDO de `jewels.ts`, onde os coeficientes por nível vivem (gate V28G.W).
   *
   * Ele estava escrito aqui, e por isso um teto de joia derivado do nível tinha
   * de repetir o 30 à mão do outro lado - que foi como o Orbe Vampírico acabou
   * com um teto declarado que o nível tornava inalcançável.
   */
  jewel: JEWEL_MAX_LEVEL,
  /**
   * Uma carta vai ao nível **6**, e é o teto final (gate V28H.C).
   *
   * LIDO de `cards.ts`, onde a progressão vive, e nunca escrito aqui: o `6` e a
   * redução de 12 pontos percentuais por nível são as duas metades da mesma
   * decisão, e um número copiado para este ficheiro seria a segunda metade a
   * envelhecer sozinha.
   *
   * A migração 0052 põe o mesmo 6 em `item_definitions.max_level`, e daí em
   * diante o gatilho de nível da 0003 recusa 0 e recusa 7 sem uma linha de
   * código nova.
   */
  card: CARD_MAX_LEVEL,
});

/* -------------------------------------------------------------------------- */
/*  Definitions                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Permanent, immutable definition id. Once a definition ships, its id never
 * changes and is never reused - owned rows reference it forever.
 */
export const itemDefinitionIdSchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);

/** Category-specific link into the existing content catalogs. Typed only
 * where a category actually needs one - never a free-form bag. */
export type ItemDefinitionMetadata =
  | { readonly kind: 'ship'; readonly shipIndex: number }
  | { readonly kind: 'cannon'; readonly cannonRarity: CannonRarity }
  | { readonly kind: 'artifact'; readonly artifactId: string }
  | { readonly kind: 'pet'; readonly petId: string; readonly stars: number }
  /** Permanent training, applied to ONE pet instance. */
  | { readonly kind: 'pet_food'; readonly counter: 'damage' | 'attackSpeed' }
  /** Unlocks one energy colour for the ACCOUNT, once. */
  | { readonly kind: 'pet_energy_sphere'; readonly unlocksId: string }
  /** Unlocks one chassis finish for the ACCOUNT, once. */
  | { readonly kind: 'pet_paint_bucket'; readonly unlocksId: string }
  /**
   * One of the five energy-system components.
   *
   * The component decides the SLOT and which attribute is guaranteed; the
   * rarity decides how MANY attributes the instance rolled. Neither decides a
   * value - those live on the instance, rolled once.
   */
  | {
      readonly kind: 'equipment';
      readonly component: EquipmentComponent;
      readonly attributeCount: number;
    }
  /** The consumable that prevents an upgrade failure from destroying an item. */
  | { readonly kind: 'equipment_stabilizer' }
  /**
   * Content that has LEFT the game.
   *
   * The definition stays so foreign keys hold and old `inventory_events`
   * still parse; `enabled: false` is what stops it being granted again.
   */
  /**
   * A cosmetic skin (ADR 0037). Carries its own id and nothing else.
   *
   * No socket count, no jewel, no attribute and no field that could hold one:
   * a skin's metadata is deliberately incapable of describing a bonus.
   */
  | { readonly kind: 'skin'; readonly skinId: string }
  | { readonly kind: 'retired'; readonly retiredIn: string };

export interface ItemDefinition {
  readonly id: string;
  readonly category: ItemCategory;
  readonly rarity: Rarity;
  readonly storage: ItemStorageKind;
  /** Inherited from `ITEM_LEVEL_CAPS`; null = level rule pending, level 1 only. */
  readonly maxLevel: number | null;
  /**
   * Whether the item may EVER be listed or traded. Currently `false` for the
   * whole catalog: the per-item tradability policy is PENDING
   * (docs/OPEN_QUESTIONS.md). Flipping to true later is additive; a wrongly
   * tradable starter item would not be.
   */
  readonly tradable: boolean;
  /** Disabled definitions exist in the catalog but cannot be granted. */
  readonly enabled: boolean;
  /** Asset id in the generated manifest; null while no art exists. */
  readonly assetKey: string | null;
  /** Bumped when the definition's gameplay-relevant content changes. */
  readonly contentVersion: number;
  /** Present only where the category actually needs a link (ships, cannons). */
  readonly metadata?: ItemDefinitionMetadata;
}

function shipDefinition(entry: ShipCatalogEntry): ItemDefinition {
  const definition: ItemDefinition = {
    // Same id as the asset on purpose: `ship1`..`ship20` are already the
    // permanent names of these twenty ships everywhere else in the project.
    id: entry.assetId,
    category: 'ship',
    rarity: entry.rarity,
    storage: 'instance',
    maxLevel: ITEM_LEVEL_CAPS.ship,
    // Ships are tradable as a KIND. The free starter copy is bound on its own
    // row instead (`item_instances.soulbound`), so a second copy of the same
    // model from a drop, a gacha pull or a purchase is an ordinary ship.
    tradable: true,
    enabled: true,
    assetKey: entry.assetId,
    contentVersion: 1,
    metadata: { kind: 'ship', shipIndex: entry.shipIndex },
  };
  return Object.freeze(definition);
}

function cannonDefinition(rarity: CannonRarity): ItemDefinition {
  const entry = CANNON_CATALOG[rarity];
  const definition: ItemDefinition = {
    id: `cannon_${rarity}`,
    category: 'cannon',
    rarity,
    storage: 'instance',
    maxLevel: ITEM_LEVEL_CAPS.cannon,
    // Same rule as ships: the kind is tradable, the free starter copy is bound
    // per instance. Every cannon in the catalogue is released and has art —
    // the disabled, artless case existed only for the planned fourth cannon,
    // and that update was cancelled (ADR 0132).
    tradable: true,
    enabled: true,
    assetKey: entry.assetId,
    contentVersion: 1,
    metadata: { kind: 'cannon', cannonRarity: rarity },
  };
  return Object.freeze(definition);
}

function artifactDefinition(artifact: ArtifactDefinition): ItemDefinition {
  const definition: ItemDefinition = {
    id: artifact.id,
    category: 'artifact',
    rarity: artifact.rarity,
    // Individually owned: two copies of Guns are two items, and combining
    // them is a deliberate act, never an automatic stack.
    storage: 'instance',
    maxLevel: ITEM_LEVEL_CAPS.artifact,
    tradable: true,
    enabled: true,
    assetKey: artifact.assetKey,
    contentVersion: 1,
    metadata: { kind: 'artifact', artifactId: artifact.id },
  };
  return Object.freeze(definition);
}

function petDefinition(pet: PetDefinition): ItemDefinition {
  const definition: ItemDefinition = {
    id: pet.id,
    category: 'pet',
    rarity: pet.rarity,
    // Individually owned: two copies of MK I are two items, and evolving them
    // is a deliberate act that consumes both.
    storage: 'instance',
    maxLevel: ITEM_LEVEL_CAPS.pet,
    // The instance carries its own evolution, training and appearance, so a
    // traded pet arrives exactly as it left.
    tradable: true,
    enabled: true,
    // The MK I art. Later forms resolve their own asset from the pet catalog;
    // this is the definition-level thumbnail the generic item wire carries.
    assetKey: pet.forms[0]?.assetKey ?? null,
    contentVersion: 1,
    metadata: { kind: 'pet', petId: pet.id, stars: pet.stars },
  };
  return Object.freeze(definition);
}

/**
 * A pet food, an Energy Sphere or a Tech Paint Bucket.
 *
 * Stackable, because one food is indistinguishable from another: quantity
 * lives in `item_stacks` rather than in individually identified rows.
 */
function petConsumableDefinition(
  entry: PetFoodDefinition | PetUnlockConsumable,
  rarity: Rarity,
  metadata: ItemDefinitionMetadata,
): ItemDefinition {
  const definition: ItemDefinition = {
    id: entry.id,
    category: 'consumable',
    rarity,
    storage: 'stack',
    // Consumables have no level rule, and inventing one would be exactly the
    // sort of guess `docs/OPEN_QUESTIONS.md` forbids.
    maxLevel: ITEM_LEVEL_CAPS.consumable,
    // Foods, Energy Spheres and Tech Paint Buckets. Stackable, so a partial
    // quantity may be offered.
    tradable: true,
    enabled: true,
    assetKey: entry.assetKey,
    contentVersion: 1,
    metadata,
  };
  return Object.freeze(definition);
}

/**
 * One of the 45 components: five families at nine rarities.
 *
 * Each carries a real, distinct design rendered from its own 3D model
 * (`packages/asset-pipeline/src/equipment3d/`), where the rarity adds structure
 * - more plates, more segments, more circuit runs, more ornament - rather than
 * repainting one shape. The same render pass produces the aligned assembly
 * layer, which is why an icon can never disagree with the machine it belongs to.
 */
function equipmentDefinition(entry: EquipmentDefinitionEntry): ItemDefinition {
  const definition: ItemDefinition = {
    id: entry.id,
    category: 'equipment',
    rarity: entry.rarity,
    // Individually owned, always: two Prismatic Núcleos rolled different
    // attributes at different values and are two different objects. A stack
    // could not express that and never will.
    storage: 'instance',
    maxLevel: ITEM_LEVEL_CAPS.equipment,
    // Tradable as a KIND. Whether a given copy may be traded right now is the
    // instance's business - an EQUIPPED one is refused by `resolveTradability`,
    // which is exactly the difference between equipment and an artifact.
    tradable: true,
    enabled: true,
    /*
     * The inventory ICON.
     *
     * The aligned assembly LAYER is a second crop of the same design and is
     * deliberately NOT here: a definition has one thumbnail, and the machine is
     * drawn by the panel through `equipmentLayerAssetKey`. Putting both on the
     * definition would make every surface that reads `assetKey` have to know
     * which one it wanted.
     */
    assetKey: entry.assetKey,
    contentVersion: 1,
    metadata: {
      kind: 'equipment',
      component: entry.component,
      attributeCount: entry.attributeCount,
    },
  };
  return Object.freeze(definition);
}

/**
 * The Quantum Stabiliser.
 *
 * Stackable: one is indistinguishable from another. It EXISTS - the vault can
 * hold it, the upgrade path reserves and consumes it, the marketplace can carry
 * it - and nothing grants one yet. That is deliberate: its drop is content, and
 * content is not invented here (see `docs/OPEN_QUESTIONS.md`).
 *
 * **It has ART since V28I.BL, and that authorises nothing.** The owner delivered
 * the master; a picture is what a surface draws when a player HAS one, and it
 * is not a source. No drop, no gacha entry, no store product and no reward was
 * added, and the sentence above still governs.
 */
const QUANTUM_STABILIZER_DEFINITION: ItemDefinition = Object.freeze<ItemDefinition>({
  id: QUANTUM_STABILIZER_ID,
  category: 'consumable',
  rarity: QUANTUM_STABILIZER_RARITY,
  storage: 'stack',
  maxLevel: ITEM_LEVEL_CAPS.consumable,
  tradable: true,
  enabled: true,
  assetKey: QUANTUM_STABILIZER_ID,
  contentVersion: 1,
  metadata: { kind: 'equipment_stabilizer' },
});

/**
 * Definitions that have LEFT the game.
 *
 * They stay in the catalog, DISABLED, and that is deliberate:
 * `item_instances` has a foreign key onto `item_definitions`,
 * `inventory_events` records their ids forever, and an append-only history
 * that no longer parses is not a history. Every gate that could bring one back
 * - granting, listing, activating, evolving - refuses by id instead.
 */
/**
 * A skin, as the vault stores one (ADR 0037).
 *
 * `maxLevel` is `null` because a skin has no level; `tradable` is `false`
 * because the first one is free and bound, and a free cosmetic that could be
 * sold would be a Gold faucet with a button on it. `rarity` is `excellent` for
 * the inventory's sorting and colour and means nothing else - there is no skin
 * rarity ladder, and inventing one here would be deciding a thing nobody has.
 */
function skinDefinition(entry: SkinDefinitionEntry): ItemDefinition {
  /*
   * O descritor de VFX vem do MANIFESTO, não é reescrito aqui.
   *
   * `SkinGachaService.poolDefects` exige `metadata.vfx`, e a alternativa seria
   * copiar partículas, cores, frequências e a escada de LOD de cada skin para
   * dentro deste catálogo - uma segunda fonte de verdade sobre a arte, livre
   * para divergir do `skin.json` que o pipeline valida.
   *
   * O manifesto é uma projeção gerada daqueles descritores, e o encontro entre
   * os dois catálogos é por `definitionId`, exatamente como a arquitetura
   * prescreve. Uma skin sem entrada no manifesto simplesmente não tem VFX aqui,
   * e `poolDefects` a recusa - que é o comportamento correto para conteúdo
   * inacabado.
   */
  const manifestEntry = SKIN_MANIFEST.entries.find(
    (candidate) => candidate.definitionId === entry.id,
  );
  return Object.freeze({
    id: entry.id,
    category: 'skin',
    rarity: 'excellent',
    storage: 'instance',
    maxLevel: ITEM_LEVEL_CAPS.skin,
    tradable: false,
    /*
     * From the CONTENT, never hard-coded true (ADR 0039).
     *
     * A skin with no PNG, no `assetKey`, no VFX descriptor and no Cash banner
     * is not grantable, and this is the flag the vault already refuses on -
     * `grantInstanceCore` throws `definition_disabled` before anything else
     * happens. Writing `true` here would have made the catalogue disagree with
     * the content file about whether a copy can be created.
     */
    enabled: entry.enabled,
    /*
     * A chave da arte, quando existe.
     *
     * Era `null` fixo enquanto nenhuma skin tinha PNG e a arte era procedural.
     * Com conteúdo real ela é a identidade que liga esta definição à entrada do
     * manifesto - e `skinReadiness` recusa quando as duas discordam.
     */
    assetKey: entry.assetKey,
    contentVersion: manifestEntry?.contentVersion ?? 1,
    metadata: {
      kind: 'skin' as const,
      skinId: entry.id,
      ...(manifestEntry ? { vfx: manifestEntry.vfx } : {}),
    },
  });
}

function retiredDefinition(id: string): ItemDefinition {
  const definition: ItemDefinition = {
    id,
    category: 'pet',
    rarity: 'anomaly',
    storage: 'instance',
    maxLevel: ITEM_LEVEL_CAPS.pet,
    // A definition that has left the game is not tradable either. `enabled`
    // already stops it, and this makes the intent explicit rather than relying
    // on one flag to imply the other.
    tradable: false,
    // The one field that matters. `enabled: false` is what the vault already
    // checks before granting, so retirement needs no new mechanism.
    enabled: false,
    assetKey: null,
    contentVersion: 2,
    metadata: { kind: 'retired', retiredIn: '4B.1' },
  };
  return Object.freeze(definition);
}

/**
 * The production catalog: only content that actually exists today - the 20
 * ships, the cannons in their four rarities (Prismatic disabled), the ten
 * artifacts, the live pets, the pet consumables, and the tombstones of
 * content that has been retired.
 */
/**
 * Uma joia no catálogo do inventário (gate V28B, revisto no V28I.BI).
 *
 * ## `enabled` diz "existe", e não "é vendida"
 *
 * As catorze de socket ficaram desativadas enquanto foram rascunho: sem nome,
 * sem efeito e sem política de comércio. Deixaram de ser. Elas têm nome
 * (`SOCKETED_NAMES`, catorze), têm master, derivado e entrada de manifesto, têm
 * mecânica implementada no duelo e na caça, e o dono aprovou a rota de
 * aquisição delas: **cometas, planetas e pacotes de boss do mundo aberto**.
 *
 * `enabled: true` é o que torna uma definição válida para EXISTIR: ser
 * concedida, ficar no cofre, subir de nível, ser equipada e ser negociada. Não é
 * uma autorização de fonte, e nunca foi — quem decide as fontes é cada fronteira
 * na sua vez:
 *
 * - **gacha**: `GACHA_CATEGORIES` é `['ship', 'cannon', 'pet']`, e uma joia não
 *   é nenhuma das três. Nenhuma joia entra numa banda por causa desta linha;
 * - **loja do sistema**: `STORE_PRODUCT_KINDS` é `['cash', 'vip']`. A loja não
 *   sabe o que é uma `ItemDefinition`, e não tem campo onde uma coubesse;
 * - **comerciante do mundo aberto**: compra capturas, e não vende itens;
 * - **cometas, planetas e bosses**: as três fronteiras que o dono aprovou, cada
 *   uma com a sua lista derivada.
 *
 * ## Negociável entre jogadores, e só entre jogadores
 *
 * `tradable: true` nas catorze: a decisão de comércio foi tomada, e é entre
 * jogadores — o mercado e o trade direto que já existem. **A soberana continua
 * `false`**: ela é única por conta e incorporada, e a exceção é deliberada,
 * como a de uma skin.
 *
 * ## `assetKey` continua nulo, e não é uma ausência de arte
 *
 * A arte das quinze existe e é servida pelo manifesto PRÓPRIO das joias
 * (`GENERATED_JEWEL_MANIFEST`), que carrega o ficheiro e a máscara. `assetKey`
 * aponta para o manifesto GERAL, onde as joias não estão — preenchê-lo daria um
 * pedido partido. Quem desenha resolve por `itemArt`, que sabe dos três
 * manifestos.
 */
function jewelItemDefinition(jewel: JewelDefinition): ItemDefinition {
  const sovereign = jewel.socketMode === 'sovereign';
  return Object.freeze({
    id: jewel.definitionId,
    category: 'jewel',
    rarity: jewel.rarity,
    storage: 'instance',
    maxLevel: ITEM_LEVEL_CAPS.jewel,
    /*
     * As catorze de socket são negociáveis entre jogadores desde o V28I.BI. A
     * soberana não é, e a razão não mudou: única por conta, incorporada, e nunca
     * uma cópia de nada.
     */
    tradable: !sovereign,
    /*
     * Todas as quinze existem. Ver o comentário acima: isto autoriza posse,
     * equipamento e comércio, e **não autoriza fonte nenhuma**.
     */
    enabled: true,
    assetKey: null,
    contentVersion: JEWEL_CONTENT_VERSION,
  });
}

/**
 * Uma carta, projetada do catálogo para o cofre (gate V28H.C).
 *
 * ## Tudo DERIVADO, nada transcrito
 *
 * O id, a raridade e a versão de conteúdo vêm de `cards.ts` e de mais lado
 * nenhum. Nome, modos e cooldown não aparecem aqui de todo: uma `ItemDefinition`
 * não tem campo para eles, e o cofre não precisa de saber quanto tempo uma carta
 * demora a voltar - ele guarda quem é dono de quê e em que nível.
 *
 * ## Intransmissível como ESPÉCIE, e ativada
 *
 * `tradable: false`, como as skins e as joias e pela mesma razão: nenhuma
 * política de comércio, loja, gacha ou mercado foi decidida para uma carta, e
 * abrir um canal depois é aditivo enquanto fechá-lo seria retirar uma coisa que
 * os jogadores já tinham. Este gate não autoriza comércio nenhum.
 *
 * `enabled: true`, ao contrário das catorze joias de rascunho: uma carta tem
 * nome fechado, raridade fechada, cooldown fechado e uma regra de fusão que
 * existe. O que ela ainda não tem é uma habilidade - e isso não é o mesmo que
 * ser um rascunho sem conteúdo.
 *
 * `assetKey: null`: os masters não existem, e apontar para arte que não está lá
 * transformaria uma imagem em falta num pedido partido.
 */
function cardItemDefinition(card: CardDefinition): ItemDefinition {
  return Object.freeze({
    id: card.definitionId,
    category: 'card',
    rarity: card.rarity,
    // Por INSTÂNCIA, nunca em pilha: a fusão consome duas linhas com identidade
    // própria e nível próprio, e uma quantidade não tem nível.
    storage: 'instance',
    maxLevel: ITEM_LEVEL_CAPS.card,
    tradable: false,
    enabled: true,
    assetKey: null,
    contentVersion: CARD_CONTENT_VERSION,
  });
}

export const ITEM_DEFINITIONS: readonly ItemDefinition[] = Object.freeze([
  ...SHIP_CATALOG.map(shipDefinition),
  ...CANNON_RARITIES.map(cannonDefinition),
  ...ARTIFACT_DEFINITIONS.map(artifactDefinition),
  ...PET_DEFINITIONS.map(petDefinition),
  ...PET_FOODS.map((food) =>
    petConsumableDefinition(food, 'epic', { kind: 'pet_food', counter: food.counter }),
  ),
  ...PET_UNLOCK_CONSUMABLES.map((entry) =>
    petConsumableDefinition(entry, entry.kind === 'energy' ? 'legendary' : 'excellent', {
      kind: entry.kind === 'energy' ? 'pet_energy_sphere' : 'pet_paint_bucket',
      unlocksId: entry.unlocksId,
    }),
  ),
  ...EQUIPMENT_DEFINITION_ENTRIES.map(equipmentDefinition),
  QUANTUM_STABILIZER_DEFINITION,
  ...SKIN_DEFINITIONS.map(skinDefinition),
  ...JEWEL_DEFINITIONS.map(jewelItemDefinition),
  ...CARD_DEFINITIONS.map(cardItemDefinition),
  ...RETIRED_PET_IDS.map(retiredDefinition),
]);

const DEFINITIONS_BY_ID: ReadonlyMap<string, ItemDefinition> = new Map(
  ITEM_DEFINITIONS.map((definition) => [definition.id, definition]),
);

export function findItemDefinition(id: string): ItemDefinition | null {
  return DEFINITIONS_BY_ID.get(id) ?? null;
}

/** Throwing lookup for code paths where an unknown id is a programming error. */
export function requireItemDefinition(id: string): ItemDefinition {
  const definition = findItemDefinition(id);
  if (!definition) {
    throw new RangeError(`unknown item definition: ${id}`);
  }
  return definition;
}

/**
 * True when `level` is legal for the definition: an integer, at least 1, and
 * within the category cap - or exactly 1 while the cap is pending.
 */
export function isItemLevelAllowed(definition: ItemDefinition, level: number): boolean {
  if (!Number.isSafeInteger(level) || level < 1) {
    return false;
  }
  return definition.maxLevel === null ? level === 1 : level <= definition.maxLevel;
}
